// SCR-STAFF-409 — اقساط و معوقات (US-BIL-101، 102): برنامه‌های اقساط شعب مجاز با فیلتر وضعیت و «فقط معوق»، جزئیات اقساط (سررسید، پرداخت‌شده، روز
// تأخیر، جریمه دیرکرد) و پرداخت قسط در صندوق یا ثبت چک قسط. تعریف برنامه از پروفایل مالی مراجعه‌کننده (روی صورتحساب باز).
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { unwrap, type BilInstallmentPlanStatus, type BilInstallmentPlanView } from '@phoenix/api-client';
import { useAccess, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, faDigits, JalaliDate, ListPage, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { ChequeDialog } from './FinanceDialogs';
import { Amount, bilKeys, cashierPatientPath, financePath, InstallmentStatusChip, SCR_STAFF_409 } from './common';

const STATUSES: readonly Exclude<BilInstallmentPlanStatus, null>[] = ['Active', 'Overdue', 'Defaulted', 'Completed', 'WrittenOff'];

export function InstallmentsPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const [params] = useSearchParams();
  const patientId = params.get('patientId') ?? undefined;
  const [status, setStatus] = useState<BilInstallmentPlanStatus | ''>('');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [selected, setSelected] = useState<BilInstallmentPlanView | null>(null);
  const [chequeFor, setChequeFor] = useState<BilInstallmentPlanView | null>(null);
  const plans = useInfiniteQuery({
    queryKey: bilKeys.plans(session?.tenantId, `${patientId ?? ''}|${status}|${overdueOnly}`),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(tenantApi.GET('/api/v1/billing/installment-plans', { params: { query: { patientId, status: status || undefined, overdueOnly: overdueOnly || undefined, cursor: pageParam, limit: 50 } } })),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const rows = plans.data?.pages.flatMap((page) => page.items);
  const current = selected ? (rows?.find((row) => row.id === selected.id) ?? selected) : null;
  const columns: DataTableColumn<BilInstallmentPlanView>[] = [
    { id: 'patient', headerKey: 'bil:deposits.columns.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'invoice', headerKey: 'bil:installments.invoice', cell: (row) => <bdi dir="ltr">{row.invoiceNumber ?? '—'}</bdi> },
    { id: 'financed', headerKey: 'bil:installments.financed', cell: (row) => <Amount money={row.financed} /> },
    { id: 'outstanding', headerKey: 'bil:installments.outstanding', cell: (row) => <Amount money={row.outstanding} /> },
    { id: 'count', headerKey: 'bil:installments.count', cell: (row) => faDigits(String(row.installmentCount)) },
    { id: 'status', headerKey: 'bil:deposits.columns.status', cell: (row) => <InstallmentStatusChip status={row.status} /> },
    {
      id: 'actions',
      headerKey: 'bil:deposits.columns.actions',
      cell: (row) => (
        <Button size="small" onClick={() => setSelected(row)} data-testid="bil-plan-open">
          {t('bil:installments.details')}
        </Button>
      ),
    },
  ];
  return (
    <ListPage
      screenId={SCR_STAFF_409}
      titleKey="bil:installments.title"
      subtitleKey="bil:installments.subtitle"
      filters={
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField select size="small" label={t('bil:deposits.columns.status')} value={status} onChange={(event) => setStatus(event.target.value as BilInstallmentPlanStatus | '')} sx={{ minWidth: 200 }}>
            <MenuItem value="">{t('bil:invoices.all')}</MenuItem>
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`bil:installment_status.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel control={<Switch checked={overdueOnly} onChange={(event) => setOverdueOnly(event.target.checked)} data-testid="bil-plans-overdue" />} label={t('bil:installments.overdue_only')} />
        </Stack>
      }
    >
      <DataTable
        captionKey="bil:installments.title"
        columns={columns}
        rows={rows}
        getRowId={(row) => row.id}
        isLoading={plans.isPending}
        error={plans.error}
        onRetry={() => void plans.refetch()}
        hasMore={plans.hasNextPage}
        onLoadMore={() => void plans.fetchNextPage()}
        isFetchingMore={plans.isFetchingNextPage}
        emptyTitleKey="bil:installments.empty"
      />
      {current ? (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }} data-testid="bil-plan-detail">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">
              {current.patientName ?? '—'} · <InstallmentStatusChip status={current.status} />
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" component={RouterLink} to={financePath(current.patientId)}>
                {t('bil:cashier.finance')}
              </Button>
              {access.can('bil.payment.receive') ? (
                <Button size="small" variant="contained" component={RouterLink} to={cashierPatientPath(current.patientId)} data-testid="bil-plan-pay">
                  {t('bil:installments.pay')}
                </Button>
              ) : null}
              {access.can('bil.cheque.manage') && current.outstanding.amount > 0 ? (
                <Button size="small" variant="outlined" onClick={() => setChequeFor(current)} data-testid="bil-plan-cheque">
                  {t('bil:installments.pay_cheque')}
                </Button>
              ) : null}
            </Stack>
          </Stack>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t('bil:installments.down_payment')} <Amount money={current.downPayment} /> · {t(`bil:guarantee.${current.guarantee}`)}
            {current.fee.amount > 0 ? (
              <>
                {' · '}
                {t('bil:installments.fee')} <Amount money={current.fee} />
              </>
            ) : null}
          </Typography>
          <Table size="small" aria-label={t('bil:installments.schedule')}>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>{t('bil:installments.due')}</TableCell>
                <TableCell>{t('bil:cashier.amount')}</TableCell>
                <TableCell>{t('bil:installments.remaining')}</TableCell>
                <TableCell>{t('bil:deposits.columns.status')}</TableCell>
                <TableCell>{t('bil:installments.days_overdue')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {current.installments.map((item) => (
                <TableRow key={item.id} data-testid="bil-installment-row" data-status={item.status}>
                  <TableCell>{faDigits(String(item.sequence))}</TableCell>
                  <TableCell>
                    <JalaliDate value={item.dueDate} />
                  </TableCell>
                  <TableCell>
                    <Amount money={item.amount} />
                  </TableCell>
                  <TableCell>
                    <Amount money={item.remaining} />
                  </TableCell>
                  <TableCell>
                    <InstallmentStatusChip status={item.status} />
                  </TableCell>
                  <TableCell>{item.daysOverdue ? faDigits(String(item.daysOverdue)) : '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      ) : null}
      {chequeFor ? (
        <ChequeDialog
          open
          patientId={chequeFor.patientId}
          installmentPlanId={chequeFor.id}
          amount={chequeFor.installments.find((item) => item.remaining.amount > 0)?.remaining.amount}
          onClose={() => setChequeFor(null)}
        />
      ) : null}
    </ListPage>
  );
}
